import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { resolveImageUrl } from "@/lib/api";
import { ArrowRight, CheckCircle2, ExternalLink, Sparkles } from "lucide-react";
import RFQModal from "@/components/RFQModal";

export default function SolutionDetail() {
  const { slug } = useParams();
  const [sol, setSol] = useState(null);
  const [rfqType, setRfqType] = useState(null);

  useEffect(() => {
    setSol(null);
    api.get(`/solutions/${slug}`).then((r) => setSol(r.data)).catch(() => setSol(false));
  }, [slug]);

  if (sol === null) {
    return <div className="min-h-[60vh] grid place-items-center text-msr-slate" data-testid="solution-loading">Loading...</div>;
  }
  if (sol === false) {
    return (
      <div className="min-h-[60vh] grid place-items-center" data-testid="solution-not-found">
        <div className="text-center">
          <h1 className="font-heading font-extrabold text-3xl mb-3">Solution not found</h1>
          <Link to="/solutions" className="btn-msr-outline">Back to Solutions</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`solution-detail-${sol.slug}`}>
      {/* HERO */}
      <section className="relative bg-msr-ink text-white overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" aria-hidden />
        <div className="absolute -top-24 -right-24 h-96 w-96 border-2 border-msr-blue/30 rotate-12" aria-hidden />
        <div className="msr-container py-20 relative grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div>
              <Link to="/solutions" className="text-xs uppercase tracking-[0.18em] text-msr-green-light hover:text-white" data-testid="solution-back-link">
                ← All Solutions
              </Link>
            </div>
            <div className="overline text-msr-green-light mt-4 block">{sol.category}</div>
            {sol.logo_image && (
              <img src={resolveImageUrl(sol.logo_image)} alt={`${sol.name} logo`} className="h-20 lg:h-24 w-auto object-contain mt-4" data-testid="solution-logo-image" />
            )}
            <h1 className="font-heading font-extrabold text-5xl lg:text-7xl tracking-tight mt-4" style={{ color: "white" }} data-testid="solution-title">{sol.name}</h1>
            <p className="text-white/70 uppercase tracking-[0.18em] text-xs mt-2 font-semibold">{sol.full_name}</p>
            <p className="text-lg text-white/85 leading-relaxed mt-6 max-w-2xl" data-testid="solution-tagline">{sol.tagline}</p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              {sol.demo_visible && sol.demo_url && (
                <a href={sol.demo_url} target="_blank" rel="noreferrer" className="btn-msr-secondary" data-testid="solution-open-demo-btn">
                  <ExternalLink size={14} /> {sol.demo_label || "Open Demo"}
                </a>
              )}
              <button onClick={() => setRfqType("demo")} className="btn-msr-outline border-white text-white hover:bg-white hover:text-msr-ink" data-testid="solution-request-demo-btn">
                Request Demo
              </button>
              {sol.free_trial_available && (
                <button onClick={() => setRfqType("trial")} className="text-sm font-semibold text-msr-green-light hover:text-white inline-flex items-center gap-1.5" data-testid="solution-free-trial-btn">
                  <Sparkles size={14} /> Start Free Trial
                </button>
              )}
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="absolute -inset-4 border-2 border-msr-green/30" aria-hidden />
            <div className="absolute -bottom-4 -right-4 h-16 w-16 bg-msr-green" aria-hidden />
            <img src={resolveImageUrl(sol.featured_image)} alt={sol.name} className="relative w-full h-[360px] object-cover" data-testid="solution-hero-image" />
          </div>
        </div>
      </section>

      {/* OVERVIEW + FEATURES */}
      <section className="bg-white border-b border-msr-line">
        <div className="msr-container py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="overline mb-3">Overview</div>
            <h2 className="font-heading font-extrabold text-3xl lg:text-4xl tracking-tight mb-6">A platform built for how Filipino businesses operate.</h2>
            <p className="text-msr-slate leading-relaxed whitespace-pre-line" data-testid="solution-long-description">{sol.long_description}</p>
          </div>
          <div className="lg:col-span-5">
            <div className="msr-card border-t-4 border-t-msr-green relative" data-testid="solution-features">
              <div className="msr-corner" aria-hidden />
              <div className="overline mb-3">Key Features</div>
              <ul className="space-y-3">
                {(sol.features || []).map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-msr-ink">
                    <CheckCircle2 size={18} className="text-msr-green flex-shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* MODULES */}
      {(sol.modules || []).length > 0 && (
        <section className="bg-msr-surface border-b border-msr-line">
          <div className="msr-container py-20">
            <div className="overline mb-3">Modules</div>
            <h2 className="font-heading font-extrabold text-3xl lg:text-4xl tracking-tight mb-10">Modular by design.</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {sol.modules.map((m, i) => (
                <div key={i} className="msr-card" data-testid={`solution-module-${i}`}>
                  <div className="msr-corner-green" aria-hidden />
                  <div className="h-9 w-9 grid place-items-center bg-msr-blue text-white text-xs font-extrabold mb-4">{String(i + 1).padStart(2, "0")}</div>
                  <div className="font-heading font-bold text-lg mb-1.5">{m.name}</div>
                  <p className="text-sm text-msr-slate leading-relaxed">{m.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PRICING PLANS */}
      {(sol.pricing_plans || []).length > 0 && (
        <section className="bg-white border-b border-msr-line">
          <div className="msr-container py-20">
            <div className="overline mb-3">Pricing</div>
            <h2 className="font-heading font-extrabold text-3xl lg:text-4xl tracking-tight mb-10">Plans that scale with your team.</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {sol.pricing_plans.map((p, i) => (
                <div key={i} className={`msr-card ${i === 1 ? "border-t-4 border-t-msr-blue" : ""}`} data-testid={`solution-plan-${p.name}`}>
                  <div className="msr-corner" aria-hidden />
                  <div className="overline text-msr-blue">{p.name}</div>
                  <div className="text-sm text-msr-slate mt-1.5">{p.audience}</div>
                  <div className="mt-5 font-heading font-extrabold text-3xl text-msr-ink">{p.price || "—"}</div>
                  <div className="text-xs text-msr-slate mt-1">Setup: {p.setup_fee || "Scoped"}</div>
                  <button onClick={() => setRfqType("subscribe")} className="mt-6 w-full btn-msr-outline py-2.5 text-xs" data-testid={`solution-plan-subscribe-${p.name}`}>
                    Subscribe / Inquire
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* GALLERY */}
      {(sol.gallery || []).length > 0 && (
        <section className="bg-msr-surface border-b border-msr-line">
          <div className="msr-container py-20">
            <div className="overline mb-3">Inside {sol.name}</div>
            <h2 className="font-heading font-extrabold text-3xl lg:text-4xl tracking-tight mb-10">Screenshots & Glimpses.</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {sol.gallery.map((g, i) => (
                <div key={i} className="relative aspect-[4/3] overflow-hidden bg-msr-mist border border-msr-line" data-testid={`solution-gallery-${i}`}>
                  <img src={resolveImageUrl(g)} alt="" className="h-full w-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ON-PREMISE */}
      <section className="bg-msr-ink text-white">
        <div className="msr-container py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="overline text-msr-green-light mb-3">On-Premise Deployment</div>
            <h2 className="font-heading font-extrabold text-3xl lg:text-4xl tracking-tight mb-5">
              Need more control & security?
            </h2>
            <p className="text-white/75 leading-relaxed max-w-2xl">{sol.on_premise_note}</p>
            {sol.on_premise_price && (
              <p className="mt-5 text-msr-green-light font-semibold text-sm uppercase tracking-[0.18em]">{sol.on_premise_price}</p>
            )}
          </div>
          <div className="lg:col-span-5 flex flex-col gap-3">
            <button onClick={() => setRfqType("onprem")} className="btn-msr-secondary" data-testid="solution-onprem-btn">
              Get an On-Premise Proposal <ArrowRight size={14} />
            </button>
            <button onClick={() => setRfqType("demo")} className="btn-msr-outline border-white text-white hover:bg-white hover:text-msr-ink" data-testid="solution-onprem-demo-btn">
              Request Demo
            </button>
            <Link to="/request-quote" className="text-sm text-msr-green-light hover:text-white text-center pt-2" data-testid="solution-onprem-quote-link">
              Or use the general Request a Quote form →
            </Link>
          </div>
        </div>
      </section>

      {rfqType && (
        <RFQModal
          inquiryType={rfqType}
          solutionSlug={sol.slug}
          solutionName={sol.name}
          onClose={() => setRfqType(null)}
        />
      )}
    </div>
  );
}
